export interface PositionInput {
  accountSize: number;
  riskPct: number;
  entry: number;
  stop: number;
  target?: number;
}

export interface PositionResult {
  /** Money put at risk if the stop is hit: account size x risk percent. */
  riskAmount: number;
  /** Distance between entry and stop, per share. */
  perShareRisk: number;
  /** Whole shares, rounded down so risk never exceeds the budget. */
  shares: number;
  positionValue: number;
  /** Reward to risk ratio from entry to target, when a target is given. */
  rewardRisk: number | null;
}

/** Position size from account size, risk percent and the entry and stop levels. Null when inputs cannot produce a size. */
export function computePositionSize(i: PositionInput): PositionResult | null {
  const { accountSize, riskPct, entry, stop, target } = i;
  if (![accountSize, riskPct, entry, stop].every((n) => Number.isFinite(n))) return null;
  if (accountSize <= 0 || riskPct <= 0 || entry <= 0 || stop <= 0) return null;
  const perShareRisk = Math.abs(entry - stop);
  if (perShareRisk === 0) return null;
  const riskAmount = (accountSize * riskPct) / 100;
  // The small epsilon stops binary float noise (for example 0.29 / 0.01) from dropping a whole share.
  const shares = Math.floor(riskAmount / perShareRisk + 1e-9);
  const rewardRisk =
    target !== undefined && Number.isFinite(target) && target > 0 ? Math.abs(target - entry) / perShareRisk : null;
  return { riskAmount, perShareRisk, shares, positionValue: shares * entry, rewardRisk };
}
